import React, { useCallback, useEffect, useRef, useState } from 'react';

import { errorMessage, fetchPending, isUnauthorized, removePhoto, resolvePending, setReviewWindow } from '../../api';
import { OFFICIAL_NICKNAME, isOfficial } from '../../official';
import { productsLabel } from '../../purchases';
import { windowLabel } from '../../reviewWindow';
import { EmoteText } from '../chat/Emotes';
import ClearConfirm from './ClearConfirm';

const REFRESH_MS = 4000; // the queue is what the admin watches while the event runs
const DEFAULT_WINDOW = 60;
const WINDOWS = [30, 60, 120, 180, 300, 600]; // seconds; the current one is added if it is another

// A purchase, as the chat card shows it. The photo is what the admin is here to check, so it
// is right there and opens full size in a tab
const Purchase = ({ purchase }) => (
  <>
    {purchase.photo ? (
      <a className='review__photo' href={purchase.photo} target='_blank' rel='noopener noreferrer' title='Ver la foto completa'>
        <img src={purchase.photo} alt='' loading='lazy' referrerPolicy='no-referrer' />
      </a>
    ) : (
      <span className='review__photo review__photo--none' aria-hidden='true'>
        🛒
      </span>
    )}
    <span className='review__purchase-body'>
      <strong className='review__nick'>{purchase.nickname}</strong> hizo una compra
      {purchase.products?.length > 0 && <span className='review__products'>{productsLabel(purchase.products)}</span>}
    </span>
  </>
);

/**
 * The review column: what is waiting on top, what the room already shows underneath.
 * Messages with blocked words never get here - they are dropped before. What nobody stops
 * goes out on its own once its window is over, so the queue is a plain list of dense rows,
 * each with three quiet actions: publish it now, drop it, or mute whoever wrote it.
 * Purchases from the sales webhook wait here too, photo included. A purchase is never
 * dropped and nobody is muted for it: the one thing the admin can do, before or after it
 * is published, is take the photo off, in case the sales system sent a bad one.
 * "Limpiar chat" empties both lists, here and on every phone, for the next event.
 */
const ReviewQueue = ({
  adminKey,
  reviewWindowSeconds = DEFAULT_WINDOW,
  published,
  status,
  onUnauthorized,
  onStateChange,
  onDelete,
  onPhotoRemoved,
  onBlock,
  onChatCleared = () => {},
  emotes = [],
}) => {
  const [pending, setPending] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clearing, setClearing] = useState(false); // the question before the whole chat goes
  // The queue polls every few seconds: a reply that lands after unmounting is dropped
  const mounted = useRef(true);
  // The published list follows the newest message unless the admin scrolled up to read
  const publishedRef = useRef(null);
  const stickToBottom = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const handleError = useCallback(
    (err) => {
      if (!mounted.current) return;
      if (isUnauthorized(err)) onUnauthorized();
      else setError(errorMessage(err));
    },
    [onUnauthorized]
  );

  const load = useCallback(async () => {
    try {
      const messages = await fetchPending(adminKey);
      if (!mounted.current) return;
      setPending(messages);
      setError(null);
    } catch (err) {
      handleError(err);
    }
  }, [adminKey, handleError]);

  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_MS);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    const list = publishedRef.current;
    if (list && stickToBottom.current) list.scrollTop = list.scrollHeight;
  }, [published]);

  const handlePublishedScroll = () => {
    const list = publishedRef.current;
    stickToBottom.current = list.scrollHeight - list.scrollTop - list.clientHeight < 40;
  };

  const resolve = async (action, ids) => {
    setBusy(true);
    try {
      await resolvePending(adminKey, { action, ids });
      setPending((prev) => (ids ? prev.filter((item) => !ids.includes(item.id)) : []));
      onStateChange();
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  // The card stays; only the photo goes, here and on every phone
  const stripPhoto = async (id) => {
    setBusy(true);
    try {
      await removePhoto(adminKey, id);
      setPending((prev) => prev.map((item) => (item.id === id ? { ...item, photo: undefined } : item)));
      onPhotoRemoved(id);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  const changeWindow = async (seconds) => {
    setBusy(true);
    try {
      await setReviewWindow(adminKey, seconds);
      onStateChange();
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  const windows = WINDOWS.includes(reviewWindowSeconds) ? WINDOWS : [...WINDOWS, reviewWindowSeconds].sort((a, b) => a - b);
  // "borrar las N" only ever drops messages; purchases go out no matter what
  const droppable = pending.filter((message) => message.type !== 'PURCHASE').length;

  return (
    <aside className='review'>
      <section className='review__section'>
        <header className='review__head'>
          <h2 className='review__title'>Revisión previa</h2>
          <select
            className='review__select'
            aria-label='Tiempo de revisión'
            title='Cuánto espera cada mensaje antes de salir solo'
            value={reviewWindowSeconds}
            disabled={busy}
            onChange={(e) => changeWindow(Number(e.target.value))}
          >
            {windows.map((seconds) => (
              <option key={seconds} value={seconds}>
                {windowLabel(seconds)}
              </option>
            ))}
          </select>
        </header>

        <div className='review__list'>
          {pending.length ? (
            pending.map((message) => {
              const purchase = message.type === 'PURCHASE';
              return (
                <p key={message.id} className={`review__pending${purchase ? ' review__row--purchase' : ''}`}>
                  {purchase ? (
                    <Purchase purchase={message} />
                  ) : (
                    <>
                      <strong className='review__nick'>{message.nickname}</strong>: <EmoteText content={message.content} emotes={emotes} linkify={false} />
                    </>
                  )}
                  <span className='review__inline-actions'>
                    <button
                      type='button'
                      className='link-action link-action--strong'
                      disabled={busy}
                      onClick={() => resolve('publish', [message.id])}
                    >
                      publicar
                    </button>
                    {purchase ? (
                      message.photo && (
                        <button type='button' className='link-action' disabled={busy} onClick={() => stripPhoto(message.id)}>
                          quitar la foto
                        </button>
                      )
                    ) : (
                      <>
                        <button type='button' className='link-action' disabled={busy} onClick={() => resolve('discard', [message.id])}>
                          borrar
                        </button>
                        <button type='button' className='link-action' disabled={busy} onClick={() => onBlock(message, 'muted')}>
                          silenciar
                        </button>
                      </>
                    )}
                  </span>
                </p>
              );
            })
          ) : (
            <p className='admin__empty'>
              Nada esperando. Cada mensaje o compra espera {windowLabel(reviewWindowSeconds)} y, si nadie lo frena, sale
              solo al chat.
            </p>
          )}
        </div>

        {pending.length > 1 && (
          <div className='review__bulk'>
            <button type='button' className='link-action link-action--strong' disabled={busy} onClick={() => resolve('publish')}>
              publicar las {pending.length}
            </button>
            {droppable > 1 && (
              <button type='button' className='link-action' disabled={busy} onClick={() => resolve('discard')}>
                borrar las {droppable}
              </button>
            )}
          </div>
        )}

        {error && <p className='form-error'>{error}</p>}
      </section>

      <section className='review__section review__section--published'>
        <header className='review__head'>
          <h2 className='review__title'>Ya publicado</h2>
          <span className='review__head-side'>
            <button type='button' className='admin__signout admin__signout--danger review__clear' disabled={clearing} onClick={() => setClearing(true)}>
              Limpiar chat
            </button>
            <span className={`status status--${status}`}>
              {status === 'connected' ? 'En vivo' : status === 'connecting' ? 'Conectando…' : 'Desconectado'}
            </span>
          </span>
        </header>

        {clearing && (
          <ClearConfirm
            adminKey={adminKey}
            scope='chat'
            warning='Borra todo el chat, acá y en todos los celulares: los mensajes, las compras publicadas, los avisos de sorteos y lo que espera revisión. No se puede deshacer.'
            confirmLabel='Borrar todo el chat'
            onCleared={(result) => {
              setClearing(false);
              setPending([]);
              onChatCleared(result);
            }}
            onCancel={() => setClearing(false)}
            onUnauthorized={onUnauthorized}
          />
        )}

        <div className='review__list review__list--published' ref={publishedRef} onScroll={handlePublishedScroll}>
          {published.length ? (
            published.map((message) => {
              const official = isOfficial(message.userId);
              const purchase = message.type === 'PURCHASE';
              return (
                <p key={message.id} className={`review__published${purchase ? ' review__row--purchase' : ''}`}>
                  {purchase ? (
                    <Purchase purchase={message} />
                  ) : (
                    <>
                      <strong className={`review__nick${official ? ' review__nick--official' : ''}`}>
                        {official ? OFFICIAL_NICKNAME : message.nickname}
                      </strong>
                      : <EmoteText content={message.content} emotes={emotes} linkify={false} />
                    </>
                  )}
                  <span className='review__inline-actions'>
                    {purchase ? (
                      message.photo && (
                        <button type='button' className='link-action' disabled={busy} onClick={() => stripPhoto(message.id)}>
                          quitar la foto
                        </button>
                      )
                    ) : (
                      <button type='button' className='link-action' onClick={() => onDelete(message.id)}>
                        borrar
                      </button>
                    )}
                    {!official && !purchase && (
                      <button type='button' className='link-action' onClick={() => onBlock(message, 'muted')}>
                        silenciar
                      </button>
                    )}
                  </span>
                </p>
              );
            })
          ) : (
            <p className='admin__empty'>Todavía no hay mensajes en la sala.</p>
          )}
        </div>
      </section>
    </aside>
  );
};

export default ReviewQueue;
